import type { Root } from './prototypeTypes';

interface ModalEntry {
	overlay: HTMLElement;
	returnFocus: HTMLElement | null;
	onRequestClose: () => void;
}

interface ModalState {
	stack: ModalEntry[];
	inertSnapshots: Map<HTMLElement, boolean>;
	pendingReturnFocus: HTMLElement | null | undefined;
	onKeydown: (event: KeyboardEvent) => void;
}

const states = new WeakMap<object, ModalState>();
const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[contenteditable="true"]',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

/** Makes inactive overlays unavailable to keyboard and assistive technology before their first open. */
export function initializeModalAccessibility(root: Root): void {
	root.querySelectorAll<HTMLElement>('.modal-overlay').forEach((overlay) => {
		initializeModalAccessibilityOverlay(overlay);
	});
	// Defensive: clear any stale inert from a previous session that may
	// have leaked through an unhandled modal close. Done at initialization
	// (after the active modals are correctly instrumented) so we never
	// re-enable the backdrop of a real dialog stack.
	clearStaleInertUnderRoot(root);
}

/** Initializes one lazily mounted dialog without scanning or changing its siblings. */
export function initializeModalAccessibilityOverlay(overlay: HTMLElement): void {
	prepareDialogSemantics(overlay);
	const active = overlay.classList.contains('active');
	overlay.setAttribute('aria-hidden', String(!active));
	if (active) overlay.removeAttribute('inert');
	else overlay.setAttribute('inert', '');
}

/**
 * Applies the WAI-ARIA modal interaction contract to every prototype modal.
 * The stack supports flows such as Register -> Legal document without making
 * individual feature modules aware of focus trapping or background isolation.
 */
export function activateModalAccessibility(root: Root, overlay: HTMLElement, onRequestClose: () => void, explicitReturnFocus?: HTMLElement | null): void {
	const rootElement = resolveRootElement(root);
	let state = states.get(rootElement);
	if (!state) {
		state = createState();
		states.set(rootElement, state);
		rootElement.addEventListener('keydown', state.onKeydown);
	}

	const existingIndex = state.stack.findIndex((entry) => entry.overlay === overlay);
	if (existingIndex >= 0) state.stack.splice(existingIndex, 1);
	const activeElement = document.activeElement;
	const inheritedReturnFocus = state.pendingReturnFocus;
	state.pendingReturnFocus = undefined;
	state.stack.push({
		overlay,
		returnFocus: explicitReturnFocus?.isConnected
			? explicitReturnFocus
			: inheritedReturnFocus !== undefined
			? inheritedReturnFocus
			: activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null,
		onRequestClose,
	});

	prepareDialogSemantics(overlay);
	overlay.removeAttribute('inert');
	overlay.setAttribute('aria-hidden', 'false');
	applyBackgroundIsolation(rootElement, state, overlay);
	queueMicrotask(() => focusInitialElement(state!, overlay));
}

export function deactivateModalAccessibility(root: Root, overlay: HTMLElement): void {
	const rootElement = resolveRootElement(root);
	const state = states.get(rootElement);
	overlay.setAttribute('aria-hidden', 'true');
	overlay.setAttribute('inert', '');
	if (!state) return;

	const index = state.stack.findIndex((entry) => entry.overlay === overlay);
	if (index < 0) return;
	const [closed] = state.stack.splice(index, 1);
	const next = state.stack.at(-1);

	if (next) {
		if (next.returnFocus && closed.overlay.contains(next.returnFocus)) next.returnFocus = closed.returnFocus;
		applyBackgroundIsolation(rootElement, state, next.overlay);
		if (index === state.stack.length) {
			queueMicrotask(() => {
				if (closed.returnFocus?.isConnected && next.overlay.contains(closed.returnFocus) && !closed.returnFocus.closest('[inert]')) {
					restoreFocusWithoutRing(closed.returnFocus);
				} else {
					focusInitialElement(state, next.overlay);
				}
			});
		}
	} else {
		restoreBackground(state);
		state.pendingReturnFocus = closed.returnFocus;
		queueMicrotask(() => {
			if (state.stack.length > 0 || state.pendingReturnFocus !== closed.returnFocus) return;
			const returnFocus = state.pendingReturnFocus;
			state.pendingReturnFocus = undefined;
			rootElement.removeEventListener('keydown', state.onKeydown);
			states.delete(rootElement);
			if (returnFocus?.isConnected && !returnFocus.closest('[inert]')) restoreFocusWithoutRing(returnFocus);
		});
	}
}

const FOCUS_RING_SUPPRESS_CLASS = 'focus-ring-suppressed';
let clearSuppressedFocusRing: (() => void) | null = null;

/**
 * Restores focus to a control after a scripted transition (modal close, sheet
 * dismiss, panel repaint) without painting the focus ring that device-level
 * keyboard-access heuristics (e.g. iOS Full Keyboard Access) add to
 * programmatically focused elements. The class clears on the first keydown or
 * pointerdown, so genuine keyboard navigation keeps its :focus-visible ring.
 * Focus flows the user drove from the keyboard should keep calling focus()
 * directly — their ring is the point.
 */
export function restoreFocusWithoutRing(element: HTMLElement | null): void {
	if (!element?.isConnected) return;
	element.classList.add(FOCUS_RING_SUPPRESS_CLASS);
	try {
		element.focus({ preventScroll: true });
	} catch {
		element.focus();
	}
	clearSuppressedFocusRing?.();
	// isConnected above implies ownerDocument, so listeners always land on the
	// element's own document — specs that build private jsdom instances (no
	// global `document`) go through here too.
	const doc = element.ownerDocument;
	const clear = () => {
		element.classList.remove(FOCUS_RING_SUPPRESS_CLASS);
		doc.removeEventListener('keydown', clear, true);
		doc.removeEventListener('pointerdown', clear, true);
		if (clearSuppressedFocusRing === clear) clearSuppressedFocusRing = null;
	};
	doc.addEventListener('keydown', clear, true);
	doc.addEventListener('pointerdown', clear, true);
	clearSuppressedFocusRing = clear;
}

function createState(): ModalState {
	const state = {} as ModalState;
	state.stack = [];
	state.inertSnapshots = new Map();
	state.pendingReturnFocus = undefined;
	state.onKeydown = (event: KeyboardEvent) => {
		const active = state.stack.at(-1);
		if (!active) return;
		if (event.key === 'Escape') {
			event.preventDefault();
			active.onRequestClose();
			return;
		}
		if (event.key === 'Tab') trapTabKey(event, active.overlay);
	};
	return state;
}

function prepareDialogSemantics(overlay: HTMLElement): void {
	const dialog = overlay.querySelector<HTMLElement>('.modal') ?? overlay;
	dialog.setAttribute('role', 'dialog');
	dialog.setAttribute('aria-modal', 'true');
	if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
	if (dialog.hasAttribute('aria-label') || dialog.hasAttribute('aria-labelledby')) return;
	const heading = dialog.querySelector<HTMLElement>('h1,h2,h3,h4');
	if (heading) {
		if (!heading.id) heading.id = `${overlay.id || 'modal'}-title`;
		dialog.setAttribute('aria-labelledby', heading.id);
	} else {
		dialog.setAttribute('aria-label', 'Dialog');
	}
}

function applyBackgroundIsolation(rootElement: HTMLElement, state: ModalState, overlay: HTMLElement): void {
	restoreBackground(state);
	// The overlay may have been an inert sibling in the previous stack frame.
	// Restore replays that snapshot, so reactivate the new topmost dialog only
	// after the snapshot has been applied.
	overlay.removeAttribute('inert');
	overlay.setAttribute('aria-hidden', 'false');
	let current: HTMLElement | null = overlay;
	while (current && current !== rootElement) {
		const parent: HTMLElement | null = current.parentElement;
		if (!parent) break;
		for (const sibling of Array.from(parent.children)) {
			if (!(sibling instanceof HTMLElement) || sibling === current) continue;
			if (!state.inertSnapshots.has(sibling)) state.inertSnapshots.set(sibling, sibling.hasAttribute('inert'));
			sibling.setAttribute('inert', '');
		}
		current = parent;
	}
}

function restoreBackground(state: ModalState): void {
	for (const [element, wasInert] of state.inertSnapshots) {
		if (wasInert) element.setAttribute('inert', '');
		else element.removeAttribute('inert');
	}
	state.inertSnapshots.clear();
}

function trapTabKey(event: KeyboardEvent, overlay: HTMLElement): void {
	const focusable = focusableElements(overlay);
	if (focusable.length === 0) {
		event.preventDefault();
		(overlay.querySelector<HTMLElement>('.modal') ?? overlay).focus({ preventScroll: true });
		return;
	}
	const activeIndex = focusable.findIndex((element) => element === document.activeElement);
	const nextIndex = activeIndex < 0
		? event.shiftKey ? focusable.length - 1 : 0
		: (activeIndex + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
	event.preventDefault();
	focusable[nextIndex]?.focus({ preventScroll: true });
}

function focusInitialElement(state: ModalState, overlay: HTMLElement): void {
	if (state.stack.at(-1)?.overlay !== overlay || !overlay.classList.contains('active')) return;
	// Input-less dialogs settle on the dialog container, never the first
	// control: parking scripted focus on a button paints its focus ring under
	// device keyboard-access heuristics, and a container focus announces the
	// dialog without endorsing any action. Explicit markers keep winning.
	const preferred = Array.from(overlay.querySelectorAll<HTMLElement>('[autofocus],[data-modal-initial-focus]')).find((element) => isActuallyVisible(element, overlay))
		?? Array.from(overlay.querySelectorAll<HTMLElement>('input:not([disabled]):not([type="hidden"]),textarea:not([disabled]),select:not([disabled])')).find((element) => isActuallyVisible(element, overlay))
		?? overlay.querySelector<HTMLElement>('.modal')
		?? focusableElements(overlay)[0]
		?? overlay;
	preferred.focus({ preventScroll: true });
}

function focusableElements(overlay: HTMLElement): HTMLElement[] {
	return Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => isActuallyVisible(element, overlay));
}

function isActuallyVisible(element: HTMLElement, overlay: HTMLElement): boolean {
	if (element.closest('[hidden],[aria-hidden="true"],[inert]')) return false;
	let current: HTMLElement | null = element;
	while (current && overlay.contains(current)) {
		const style = window.getComputedStyle(current);
		if (style.display === 'none' || style.visibility === 'hidden') return false;
		if (current === overlay) break;
		current = current.parentElement;
	}
	return true;
}

function resolveRootElement(root: Root): HTMLElement {
	return (root as Document).body ?? root as HTMLElement;
}

/**
 * Forcibly clear stale `inert` from any non-modal subtree under root.
 *
 * The two inert state machines (modalAccessibility + mobileBottomDockController)
 * snapshot/restore backgrounds when their surfaces open or close. A failure
 * mode where a modal closes without invoking the matching deactivate path
 * (e.g. an unhandled click that bypasses `closeModal`, an early-return from a
 * history popstate, or a destroy() during teardown) leaves the snapshot
 * instrumented but never cleared. The article view behind the closed surface
 * stays inert, and any click on it (Listen / Watch / chapter controls) is
 * swallowed by the browser. This util is a defensive pass used at well-known
 * entry points (article view mounting, the bottom dock's first sync) so the
 * transient stash can't leak into the next interaction.
 *
 * The pass never touches descendants of an active modal, so it cannot
 * re-enable the backdrop of a real dialog stack.
 */
export function clearStaleInertUnderRoot(root: Root): void {
	const rootElement = resolveRootElement(root);
	const doc = rootElement.ownerDocument ?? document;
	if (!doc) return;
	const activeModal = rootElement.querySelector<HTMLElement>('.modal-overlay.active');
	// Bound the scan to descendants of rootElement so we never disable inert
	// on something outside the prototype tree.
	for (const element of rootElement.querySelectorAll<HTMLElement>('[inert]')) {
		if (activeModal && activeModal.contains(element)) continue;
		// Keep inert on stacked modals (waiting dialogs) — the WAI-ARIA modal
		// stack may inactive-overlay siblings behind the active dialog.
		if (element.classList.contains('modal-overlay') && !element.classList.contains('active')) continue;
		element.removeAttribute('inert');
	}
	// Also clear inertSnapshot bookkeeping so future activations don't replay
	// a stale wasInert=true to e.g. .app-layout when the modal closes. The
	// states map is keyed by rootElement; if a snapshot never had a chance to
	// run restoreBackground, the entry below is stale and the next modal
	// activation would re-record the same stale state.
	const state = states.get(rootElement);
	if (state) state.inertSnapshots.clear();
}
